// src/components/ProductActions.tsx
import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, Dropdown, Spinner } from 'react-bootstrap';
import { FaTrash, FaEllipsis } from 'react-icons/fa6';
import { FaEdit } from 'react-icons/fa';
import useProductApi, { Product } from '../../../managers/ProductManager';
import { useShoppingCart } from '../../../context/ShoppingCartContext';
import { User, UserRoles } from '../../../managers/UserManager';
import UserAccess from '../../../auth/UserAccess';
import ProductEditor from './ProductEditor';

interface ProductActionsProps {
  product: Product;
  currentUser: User;
  /**
   * Called whenever this component edits or deletes a product:
   * - on edit: updater(prev => prev.map(...updated...))
   * - on delete: updater(prev => prev.filter(...))
   */
  onProductsChange?: (updater: (prev: Product[]) => Product[]) => void;
}

const ProductActions: React.FC<ProductActionsProps> = ({
  product,
  currentUser,
  onProductsChange
}) => {
  const { deleteProduct } = useProductApi();

  const [editing, setEditing] = useState<Product | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleOpenEditor = () => {
    setEditing(product);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    // Don't clear editing state yet - wait for modal animation to complete
  };

  const handleSave = (updated: Product) => {
    if (onProductsChange) {
      onProductsChange(prev =>
        prev.map(p => (p.productId === updated.productId ? updated : p))
      );
    }
    setShowEditor(false);
    // Don't clear editing state yet - wait for modal animation to complete
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteProduct(confirmDelete.productId!, confirmDelete.userId!);
      if (onProductsChange) {
        onProductsChange(prev =>
          prev.filter(p => p.productId !== confirmDelete.productId)
        );
      }
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <>
      {/* Actions: single ellipsis dropdown */}
      <Dropdown align="end">
        <Dropdown.Toggle
          as={Button}
          size="sm"
          variant="light"
          className="no-caret action-toggle"
          title="More actions"
        >
          <FaEllipsis className="fs-5" />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {/* Edit (SUPER_ADMIN or CREATOR) */}
          <UserAccess
            user={currentUser}
            roles={[UserRoles.SUPER_ADMIN, UserRoles.CREATOR]}
            operation="any"
          >
            <Dropdown.Item onClick={handleOpenEditor}>
              <FaEdit className="me-2" />
              Edit
            </Dropdown.Item>
          </UserAccess>

          {/* Delete (owner or SUPER_ADMIN/CREATOR) */}
          <UserAccess
            user={currentUser}
            roles={[UserRoles.SUPER_ADMIN, UserRoles.CREATOR]}
            operation="any"
            ownerId={product.userId}
          >
            <Dropdown.Item
              onClick={() => setConfirmDelete(product)}
              disabled={deleting}
            >
              <FaTrash className="me-2" />
              Delete
            </Dropdown.Item>
          </UserAccess>
        </Dropdown.Menu>
      </Dropdown>

      {/* Editor Modal */}
      {editing && (
        <ProductEditor
          product={editing}
          currentUser={currentUser}
          onClose={handleCloseEditor}
          onSave={handleSave}
          show={showEditor}
          onExited={() => setEditing(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={!!confirmDelete}
        onHide={() => setConfirmDelete(null)}
        centered
      >
        <ModalHeader closeButton>Confirm Deletion</ModalHeader>
        <ModalBody className="text-center">
          <p>
            Are you sure you want to delete “{confirmDelete?.name}”?
          </p>
          <div className="d-flex justify-content-center gap-2 mt-3">
            <Button
              variant="secondary"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProductActions;
